import React, { Suspense, Component } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { CircularProgress, Box, Typography } from "@mui/material";
import Routes from "./routes";
import { AuthProvider } from "./contexts/AuthContext";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { DirectionProvider } from "./contexts/DirectionContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// Import i18n configuration
import "./i18n/config";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
          textAlign="center"
        >
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong loading the application
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Please refresh the page to try again
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to access location
const AppContent = () => {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith("/auth");

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <CssBaseline />
      <Routes />
    </Box>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <LanguageProvider>
              <DirectionProvider>
                <SnackbarProvider>
                  <Suspense
                    fallback={
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="100vh"
                      >
                        <CircularProgress />
                      </Box>
                    }
                  >
                    <AppContent />
                  </Suspense>
                </SnackbarProvider>
              </DirectionProvider>
            </LanguageProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;

